import { getUserProfileByUid, updateUserProfile } from '../firebase/firestore';
import { getKinshipGroupId, KinshipGroupId } from '../i18n/kinshipGroups';

/**
 * Single source of truth for remembering a member's chosen kinship group.
 *
 * - localStorage `selectedKinshipGroup` — instant, works signed-out, and is
 *   what the sidebar / learning pages already read.
 * - Firestore profile `kinshipGroup` — follows the member across devices.
 * - `kinship-group-changed` window event — keeps mounted components (the
 *   sidebar lives across routes) in sync without prop drilling.
 */

export const KIN_CHANGED_EVENT = 'kinship-group-changed';
export const KIN_STORAGE_KEY = 'selectedKinshipGroup';

/** Canonical lowercase names used by the sidebar picker and post filters. */
const KIN_ID_TO_NAME: Record<KinshipGroupId, string> = {
  yellowThunderbirds: 'yellow thunderbirds',
  redThunderbirds: 'red thunderbirds',
  blackThunderbirds: 'black thunderbirds',
  whiteThunderbirds: 'white thunderbirds',
  yellowOak: 'yellow oak tree',
  redOak: 'red oak tree',
  blackOak: 'black oak tree',
  whiteOak: 'white oak tree',
  yellowWhale: 'yellow whale',
  redWhale: 'red whale',
  blackWhale: 'black whale',
  whiteWhale: 'white whale'
};

/** Map any stored/legacy spelling ("Yellow Thunder Birds") to the canonical name. */
export const normalizeKinName = (raw?: string | null): string | null => {
  if (!raw || !raw.trim()) return null;
  const id = getKinshipGroupId(raw);
  return id ? KIN_ID_TO_NAME[id] : raw.trim().toLowerCase();
};

export const getSavedKin = (): string | null => {
  try {
    return localStorage.getItem(KIN_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Remember a kin choice (or `null` to release it) everywhere at once.
 * The Firestore write is best-effort — a signed-out visitor still gets
 * local persistence.
 */
export const persistKinSelection = async (
  kin: string | null,
  uid?: string | null
): Promise<void> => {
  const name = normalizeKinName(kin);

  try {
    if (name) {
      localStorage.setItem(KIN_STORAGE_KEY, name);
    } else {
      localStorage.removeItem(KIN_STORAGE_KEY);
    }
  } catch {
    /* private mode — the event + profile write still apply */
  }

  window.dispatchEvent(new CustomEvent<string | null>(KIN_CHANGED_EVENT, { detail: name }));

  if (uid) {
    try {
      const profile = await getUserProfileByUid(uid);
      if (profile) {
        await updateUserProfile(profile.id, { kinshipGroup: name || '' });
      }
    } catch (error) {
      console.error('Error saving kinship group to profile:', error);
    }
  }
};
